import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { appRoutes } from '@/routes';
import { Container, Alert } from 'reactstrap';
import { buttonVariants, Button as CustomButton } from '@components/Button';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import firebase from '../../config/firebase.config';
import httpClient from '../../config/httpClient';
import styles from './CreateEmployees.module.scss';
import { Dropdown, borderedSelectStyles } from '@/components/Dropdown';
import { PillDropdown } from '@components/PillDropdown';
import { ImportMethodSelector } from '@components/ImportMethodSelector';
import { CsvImportDropzone } from '@components/CsvImportDropzone';
import { EmployeeFormRow } from '@components/EmployeeFormRow';
import { AddedEmployeesList } from '@components/AddedEmployeesList';
import { SelectedFileCard } from '@components/SelectedFileCard';
import { InfoBox } from '@components/InfoBox';
import { ResultsTable } from './components/ResultsTable.component';
import { ROLE_OPTIONS } from '@/constants/roles';
import { validateCsvRows } from '@/utils/employeeImport';
import { useTimezones } from '@/hooks/useTimezones';
import { useUserCompanies } from '@/hooks/useUserCompanies';
import { useUserTeamsForCompany } from '@/hooks/useUserTeamsForCompany';
import { useStealthMode } from '@/hooks/useStealthMode';
import { useEmployeePageCompanyTeamUrlSync } from '@/hooks/useEmployeePageUrlSync';
import {
  BsPersonPlusFill,
  BsFiletypeCsv,
  BsBuildingsFill,
} from 'react-icons/bs';
import { FiPlus, FiInfo, FiUsers } from 'react-icons/fi';
import { IS_DEMO_MODE } from '@/utils/demoMode';
import {
  DEMO_SELECTED_COMPANY,
  DEMO_TEAM_OPTIONS,
} from './CreateEmployees.demoData';

// Note: intentionally not src/utils/employeeImport.js's makeEmptyEmployee —
// that shape includes `team`, which this page never uses (department is
// chosen once for the whole batch, not per row).
const makeEmptyManualEmployee = () => ({
  email: '',
  name: '',
  timeZone: '',
  role: '',
  deskId: '',
  employeeId: '',
});

const IMPORT_METHODS = [
  {
    key: 'uploadExcel',
    icon: <BsFiletypeCsv size={22} color="#039855" />,
    iconBg: '#ecfdf3',
    title: 'Import via Excel/CSV',
    desc: 'Upload a file to add multiple employees at once.',
    enabled: true,
  },
  {
    key: 'manualEntry',
    icon: <BsPersonPlusFill size={22} color="#3363F5" />,
    iconBg: '#eff4ff',
    title: 'Add Employees Manually',
    desc: 'Enter each employee one by one.',
    enabled: true,
  },
  {
    key: 'integration',
    icon: <BsBuildingsFill size={22} color="#6938EF" />,
    iconBg: '#f4f3ff',
    title: 'Import from Integrations',
    desc: 'Import users from Google Workspace, Microsoft 365 or other tools.',
    enabled: true,
  },
];

const EXCEL_PREVIEW_COLUMNS = [
  { key: 'email', label: 'Email' },
  { key: 'name', label: 'Name' },
  { key: 'timeZone', label: 'TimeZone' },
  { key: 'role', label: 'Role' },
  { key: 'deskId', label: 'Desk ID' },
  { key: 'employeeId', label: 'Employee ID' },
];

export const CreateEmployeesComponent = () => {
  const history = useHistory();
  const location = useLocation();
  const authState = useSelector((state) => ({ ...state.auth }));
  const userProfile = authState.profile;
  const companyList = userProfile?.companyList || [];
  const userUID = userProfile?.id;
  const activeCompanyId = userProfile?.activeCompany?.companyId;
  const activeTeamId = useSelector(
    (s) => s.team?.active?.activeTeamid ?? s.team?.active?.activeTeamId
  );

  // State
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedTeam, setSelectedTeam] = useState(null);

  const {
    companyOptions,
    loading: loadingCompanies,
    error: companiesError,
  } = useUserCompanies(userUID);
  const {
    teamOptions,
    loading: loadingTeams,
    error: teamsError,
  } = useUserTeamsForCompany(userUID, selectedCompany?.value);

  useEmployeePageCompanyTeamUrlSync({
    companyOptions,
    teamOptions,
    selectedCompany,
    selectedTeam,
    setSelectedCompany,
    setSelectedTeam,
    history,
    location,
    defaultCompanyId: activeCompanyId,
    defaultTeamId: activeTeamId,
    teamDefaultOnlyWhenCompanyId: activeCompanyId,
  });

  // Demo mode: no real company/team exists for the demo profile, so
  // useUserCompanies/useUserTeamsForCompany return empty — override at the
  // point of use so the Department dropdown and form controls aren't stuck
  // disabled/empty. Real hooks/state above keep running unchanged.
  const displaySelectedCompany = IS_DEMO_MODE
    ? selectedCompany || DEMO_SELECTED_COMPANY
    : selectedCompany;
  const displaySelectedTeam = IS_DEMO_MODE
    ? selectedTeam || DEMO_TEAM_OPTIONS[0]
    : selectedTeam;
  const displayTeamOptions = IS_DEMO_MODE ? DEMO_TEAM_OPTIONS : teamOptions;
  const displayLoadingTeams = IS_DEMO_MODE ? false : loadingTeams;

  const { stealthMode, loading: stealthLoading } = useStealthMode();
  const [activeTab, setActiveTab] = useState('uploadExcel');

  // Excel upload state
  const [excelFile, setExcelFile] = useState(null);
  const [excelFileName, setExcelFileName] = useState('');

  // Manual form state — rows currently being typed into
  const [editingRows, setEditingRows] = useState([makeEmptyManualEmployee()]);
  // Confirmed employees staged for submission (manual entry), or a preview
  // of what's inside the selected file (Excel/CSV — informational only,
  // the raw file is what actually gets submitted for that path)
  const [addedEmployees, setAddedEmployees] = useState([]);
  const [rowFieldErrors, setRowFieldErrors] = useState({});

  // Results state
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  // Timezone options
  const timezoneOptions = useTimezones();

  // Download Excel template (rich .xlsx with a second Instructions sheet —
  // kept as its own function rather than CsvImportDropzone's default plain
  // CSV template, since this page's template is a two-sheet workbook)
  const downloadExcelTemplate = () => {
    const templateData = [
      {
        email: 'john.doe@company.com',
        name: 'John Doe',
        timeZone: 'America/New_York',
        role: 'Member',
        deskId: 'DESK-001',
        employeeId: 'EMP-001',
      },
      {
        email: 'jane.smith@company.com',
        name: 'Jane Smith',
        timeZone: 'Europe/London',
        role: 'Manager',
        deskId: '',
        employeeId: '',
      },
    ];
    const worksheet = XLSX.utils.json_to_sheet(templateData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Employees');

    // Add Instructions sheet
    const instructions = [
      {
        Column: 'email',
        Required: 'Yes',
        Description: 'Valid email address for each employee',
      },
      {
        Column: 'name',
        Required: 'Yes',
        Description: 'Full name of the employee',
      },
      {
        Column: 'timeZone',
        Required: 'Yes',
        Description: 'IANA timezone (e.g., America/New_York, Asia/Kolkata)',
      },
      {
        Column: 'role',
        Required: 'Yes',
        Description: 'Member, Manager, or Admin',
      },
      {
        Column: 'deskId',
        Required: 'No',
        Description: 'Optional desk identifier',
      },
      {
        Column: 'employeeId',
        Required: 'No',
        Description: 'Optional employee ID',
      },
    ];
    const instructionsSheet = XLSX.utils.json_to_sheet(instructions);
    XLSX.utils.book_append_sheet(workbook, instructionsSheet, 'Instructions');

    const excelBuffer = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array',
    });
    const blob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    saveAs(blob, 'employee_template.xlsx');
  };

  // Check if user is Admin
  useEffect(() => {
    const isAdmin =
      userProfile?.activeCompany?.role === 'Admin' ||
      companyList.some((company) => company.role === 'Admin');

    if (!isAdmin && !authState.loading) {
      setError('Access denied. Only Admin users can create employees.');
    }
  }, [userProfile, companyList, authState.loading]);

  useEffect(() => {
    if (!selectedCompany?.value) {
      setSelectedTeam(null);
    }
  }, [selectedCompany?.value]);

  // Clear the captured Excel/CSV file and return to the dropzone
  const clearExcelFile = () => {
    setExcelFile(null);
    setExcelFileName('');
  };

  // Convert file to base64
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64String = reader.result;
        resolve(base64String);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  // Handle manual form - add an editing row
  const addEditingRow = () => {
    setEditingRows([...editingRows, makeEmptyManualEmployee()]);
  };

  // Handle manual form - remove an editing row
  const removeEditingRow = (index) => {
    setEditingRows(editingRows.filter((_, i) => i !== index));
    setRowFieldErrors((prev) => {
      const next = {};
      Object.keys(prev).forEach((key) => {
        const i = Number(key);
        if (i < index) next[i] = prev[i];
        else if (i > index) next[i - 1] = prev[i];
      });
      return next;
    });
  };

  // Handle manual form - update a field on an editing row, clearing that
  // field's error highlight the moment the user starts fixing it
  const updateEditingRow = (index, field, value) => {
    const updated = [...editingRows];
    updated[index] = { ...updated[index], [field]: value };
    setEditingRows(updated);

    if (rowFieldErrors[index]?.[field]) {
      const rowErrors = { ...rowFieldErrors[index] };
      delete rowErrors[field];
      setRowFieldErrors({ ...rowFieldErrors, [index]: rowErrors });
    }
  };

  // Validate the currently-filled editing rows and move the valid ones into
  // `addedEmployees` (the staged/submittable list). Rows that fail stay in
  // place with the specific invalid field(s) highlighted inline — blank
  // rows are left untouched either way.
  const handleAddEmployees = () => {
    const nonBlankIndices = [];
    const nonBlankRows = [];
    editingRows.forEach((emp, i) => {
      const isBlank = !(
        emp.email ||
        emp.name ||
        emp.timeZone ||
        emp.role ||
        emp.deskId
      );
      if (!isBlank) {
        nonBlankIndices.push(i);
        nonBlankRows.push(emp);
      }
    });
    if (!nonBlankRows.length) return;

    const validated = validateCsvRows(nonBlankRows);

    const newFieldErrors = {};
    const validRows = [];
    const remainingRows = [];

    editingRows.forEach((emp, i) => {
      const nonBlankPos = nonBlankIndices.indexOf(i);
      if (nonBlankPos === -1) {
        remainingRows.push(emp);
        return;
      }
      const { _errors, _fieldErrors, ...validatedEmp } = validated[nonBlankPos];
      if (_errors.length === 0) {
        validRows.push(validatedEmp);
      } else {
        newFieldErrors[remainingRows.length] = _fieldErrors;
        remainingRows.push(validatedEmp);
      }
    });

    setRowFieldErrors(newFieldErrors);
    if (validRows.length) {
      setAddedEmployees([...addedEmployees, ...validRows]);
    }
    setEditingRows(
      remainingRows.length ? remainingRows : [makeEmptyManualEmployee()]
    );
  };

  const removeAddedEmployee = (index) => {
    setAddedEmployees(addedEmployees.filter((_, i) => i !== index));
  };

  // Parse Excel and validate duplicate deskIds (Excel upload)
  const validateExcelDeskIds = (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          const rows = XLSX.utils.sheet_to_json(worksheet, { raw: false });
          if (!rows || rows.length === 0) {
            resolve({ valid: true });
            return;
          }
          const deskIdHeaderAliases = ['deskid', 'desk id', 'desk_id'];
          const headerKeys = Object.keys(rows[0]);
          const deskIdKey = headerKeys.find(
            (k) =>
              deskIdHeaderAliases.includes(k.toLowerCase().trim()) ||
              k.trim() === 'deskId'
          );
          const deskIdSet = new Set();
          for (let i = 0; i < rows.length; i++) {
            const raw = deskIdKey ? rows[i][deskIdKey] : undefined;
            const value = raw != null ? String(raw).trim() : '';
            if (value === '') continue;
            if (deskIdSet.has(value)) {
              resolve({
                valid: false,
                message: `Duplicate deskId in file: "${value}" appears in multiple rows (e.g. row ${
                  i + 2
                }).`,
              });
              return;
            }
            deskIdSet.add(value);
          }
          resolve({ valid: true });
        } catch (err) {
          resolve({
            valid: false,
            message: `Could not parse file for validation: ${err.message}`,
          });
        }
      };
      reader.onerror = () =>
        resolve({ valid: false, message: 'Failed to read file' });
      reader.readAsArrayBuffer(file);
    });
  };

  // Submit form (stealth on: provision accounts directly; stealth off: email invitations)
  const handleSubmit = async () => {
    setError(null);
    setResult(null);
    if (
      !userProfile?.subscription?.isTrialActive &&
      userProfile?.subscription?.status !== 'active'
    ) {
      setError(
        'Your subscription is not active. Please activate your subscription to create employees.'
      );
      return;
    }
    if (!displaySelectedCompany?.value || !displaySelectedTeam?.value) {
      setError('Please select both company and department');
      return;
    }

    if (stealthLoading) {
      setError('Company settings are still loading. Please try again shortly.');
      return;
    }

    const useInvitationFlow = !stealthMode;

    setLoading(true);

    try {
      // Get Firebase ID token
      const user = firebase.auth().currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }
      const idToken = await user.getIdToken();

      let requestBody;

      if (activeTab === 'uploadExcel') {
        // Excel upload
        if (!excelFile) {
          setError('Please select an Excel file');
          setLoading(false);
          return;
        }

        const excelDeskIdValidation = await validateExcelDeskIds(excelFile);
        if (!excelDeskIdValidation.valid) {
          setError(excelDeskIdValidation.message);
          setLoading(false);
          return;
        }

        const fileBase64 = await fileToBase64(excelFile);
        requestBody = {
          companyId: displaySelectedCompany?.value,
          teamId: displaySelectedTeam?.value,
          fileBase64,
          fileName: excelFileName,
        };
      } else {
        // Manual form — submits the staged/added list, already validated
        // when each row was added via handleAddEmployees
        if (addedEmployees.length === 0) {
          setError('Please add at least one employee');
          setLoading(false);
          return;
        }

        requestBody = {
          companyId: displaySelectedCompany?.value,
          teamId: displaySelectedTeam?.value,
          source: 'form',
          employees: addedEmployees,
          invitedByName: userProfile?.name,
        };
      }

      const endpoint = useInvitationFlow
        ? '/sendAddEmployeeInvites'
        : '/createEmployeesFromExcel';

      const response = await httpClient.post(endpoint, requestBody, {
        headers: { Authorization: `Bearer ${idToken}` },
      });
      const data = {
        ...response.data,
        _flow: useInvitationFlow ? 'invite' : 'direct',
      };

      setResult(data);

      if (!useInvitationFlow) {
        if (data.success && data.data?.created?.length) {
          const toSend = data.data.created.filter(
            (emp) => emp.role === 'Admin' || emp.role === 'Manager'
          );
          for (const emp of toSend) {
            try {
              await firebase.auth().sendPasswordResetEmail(emp.email);
            } catch (e) {
              console.warn('Could not send password reset to', emp.email, e);
            }
          }
        }
      }

      const shouldResetForm = (() => {
        if (!data.success || !data.data?.stats) return false;
        if (useInvitationFlow) {
          return data.data.stats.errors === 0;
        }
        return data.data.stats.failed === 0;
      })();

      if (shouldResetForm) {
        if (activeTab === 'uploadExcel') {
          clearExcelFile();
          setAddedEmployees([]);
        } else {
          setEditingRows([makeEmptyManualEmployee()]);
          setAddedEmployees([]);
        }
      }
    } catch (err) {
      console.error('Error creating employees:', err);
      const data = err.response?.data;
      let errorMessage = 'An error occurred while creating employees';
      if (data) {
        errorMessage = data.message || errorMessage;
        if (
          data.errors &&
          Array.isArray(data.errors) &&
          data.errors.length > 0
        ) {
          const details = data.errors.map(
            (e) =>
              `Row ${e.row}${
                e.email && e.email !== 'N/A' ? ` (${e.email})` : ''
              }: ${e.error}`
          );
          errorMessage = `${errorMessage}\n\n${details.join('\n')}`;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Check if user is Admin
  const isAdmin =
    userProfile?.activeCompany?.role === 'Admin' ||
    companyList.some((company) => company.role === 'Admin');

  if (!isAdmin && !authState.loading) {
    return (
      <Container className="mt-5">
        <Alert color="danger">
          <h4>Access Denied</h4>
          <p>Only Admin users can create employees.</p>
        </Alert>
      </Container>
    );
  }

  const uploadExcelTab = () => {
    if (excelFile) {
      return (
        <SelectedFileCard
          file={excelFile}
          onReplace={() => {
            clearExcelFile();
            setAddedEmployees([]);
          }}
          onRemove={() => {
            clearExcelFile();
            setAddedEmployees([]);
            setError(null);
          }}
        />
      );
    }

    return (
      <CsvImportDropzone
        onImport={({ file, validRows }) => {
          setExcelFile(file);
          setExcelFileName(file.name);
          setAddedEmployees(validRows);
          setError(null);
        }}
        previewColumns={EXCEL_PREVIEW_COLUMNS}
        onDownloadTemplate={downloadExcelTemplate}
        allowConfirmWithErrors
      />
    );
  };

  const manualEntryTab = () => {
    return (
      <div className={styles.manualEntryTabContainer}>
        <div className={styles.employeeRowList}>
          {editingRows.map((emp, index) => (
            <EmployeeFormRow
              key={index}
              row={emp}
              index={index}
              timezoneOptions={timezoneOptions}
              roleOptions={ROLE_OPTIONS}
              onChange={updateEditingRow}
              onRemove={removeEditingRow}
              canRemove={editingRows.length > 1}
              errors={rowFieldErrors[index] || {}}
            />
          ))}
        </div>

        <div className={styles.manualEntryActions}>
          <CustomButton
            text="Add Another Employee"
            icon={<FiPlus />}
            variant={buttonVariants.ghost}
            size="sm"
            onClick={addEditingRow}
            disabled={
              !displaySelectedCompany?.value || !displaySelectedTeam?.value
            }
          />
          <CustomButton
            text="Add Employee"
            variant={buttonVariants.primary}
            size="sm"
            onClick={handleAddEmployees}
            disabled={
              !displaySelectedCompany?.value || !displaySelectedTeam?.value
            }
          />
        </div>
      </div>
    );
  };

  return (
    <>
      <div className={styles.createEmployeesPage}>
        <div className={styles.createEmployeesFormContainer}>
          <div className={styles.createEmployeesFormDropdownContainer}>
            {/* Company dropdown commented out — company now follows the
                header's active company (selectedCompany still defaults to
                activeCompanyId via useEmployeePageCompanyTeamUrlSync, same
                as EmployeeList). */}
            {/* <Dropdown
              label="Company *"
              value={selectedCompany}
              selectStyles={borderedSelectStyles}
              options={companyOptions}
              onChange={(option) => {
                setSelectedCompany(option);
                setSelectedTeam(null);
                setResult(null);
                setError(null);
              }}
              placeholder={
                loadingCompanies ? 'Loading companies...' : 'Select Company'
              }
            /> */}
            <PillDropdown
              icon={<FiUsers />}
              label="Department"
              value={displaySelectedTeam?.value ?? null}
              options={displayTeamOptions}
              loading={displayLoadingTeams}
              disabled={!displaySelectedCompany || displayLoadingTeams}
              placeholder="Select Department"
              onChange={(val) => {
                setSelectedTeam(
                  displayTeamOptions.find((o) => o.value === val) || null
                );
                setResult(null);
                setError(null);
              }}
            />
          </div>
          <div className={styles.createEmployeesTabBarContainer}>
            <ImportMethodSelector
              methods={IMPORT_METHODS}
              selectedMethod={activeTab}
              onSelect={(key) => {
                if (key === 'integration') {
                  history.push(appRoutes().integrations);
                  return;
                }
                setActiveTab(key);
                setResult(null);
                setError(null);
                setRowFieldErrors({});
                setAddedEmployees([]);
                clearExcelFile();
              }}
            />
            <div className="mt-3">
              {activeTab === 'uploadExcel'
                ? uploadExcelTab()
                : manualEntryTab()}
            </div>

            <div className="mt-3">
              <AddedEmployeesList
                employees={addedEmployees}
                onRemove={removeAddedEmployee}
              />
            </div>

            {(error || companiesError || teamsError) && (
              <Alert color="danger" className="mt-3">
                <pre style={{ whiteSpace: 'pre-wrap' }}>
                  {error || companiesError || teamsError || ''}
                </pre>
              </Alert>
            )}
            {!stealthLoading && !stealthMode && (
              <div className="mt-3">
                <InfoBox variant="info" icon={<FiInfo size={16} />}>
                  People you add here receive an email invitation. They sign up
                  and accept the invite to join the selected department (or the
                  company, if the role is Admin).
                </InfoBox>
              </div>
            )}
            {!stealthLoading && (
              <Alert color="secondary" className="mt-2 mb-0">
                Add Employee is for creating new employees only. If this email
                already exists, use Employee List - Add to another department.
              </Alert>
            )}

            <CustomButton
              text={
                stealthLoading
                  ? 'Loading…'
                  : stealthMode
                    ? 'Add employees'
                    : 'Send invitations'
              }
              variant={buttonVariants.primary}
              loading={loading}
              size="sm"
              onClick={handleSubmit}
              disabled={
                !displaySelectedCompany?.value ||
                !displaySelectedTeam?.value ||
                stealthLoading
              }
              className="mt-3"
            />
            {/* Results Display */}
            {result && (
              <div className="mt-4">
                <Alert color={result.success ? 'success' : 'warning'}>
                  <h5>{result.message}</h5>
                  {result.data &&
                    result.data.stats &&
                    result._flow !== 'invite' && (
                      <div className="mt-2">
                        <p>
                          <strong>Total:</strong> {result.data.stats.total} |{' '}
                          <strong>Created:</strong> {result.data.stats.created}{' '}
                          | <strong>Failed:</strong> {result.data.stats.failed}
                        </p>
                      </div>
                    )}
                  {result.data &&
                    result.data.stats &&
                    result._flow === 'invite' && (
                      <div className="mt-2">
                        <p>
                          <strong>Total:</strong> {result.data.stats.total} |{' '}
                          <strong>Sent or updated:</strong>{' '}
                          {result.data.stats.sentOrUpdated} |{' '}
                          <strong>Skipped:</strong> {result.data.stats.skipped}{' '}
                          | <strong>Email issues:</strong>{' '}
                          {result.data.stats.partial} | <strong>Errors:</strong>{' '}
                          {result.data.stats.errors}
                        </p>
                      </div>
                    )}
                </Alert>

                {result._flow !== 'invite' &&
                  result.data &&
                  result.data.created &&
                  result.data.created.length > 0 && (
                    <ResultsTable
                      title="Created Employees:"
                      columns={[
                        { key: 'row', label: 'Row' },
                        { key: 'email', label: 'Email' },
                        { key: 'name', label: 'Name' },
                        { key: 'userId', label: 'UserId' },
                        { key: 'licenseGuid', label: 'License GUID' },
                      ]}
                      rows={result.data.created}
                    />
                  )}

                {result._flow === 'invite' &&
                  result.data &&
                  Array.isArray(result.data.results) &&
                  result.data.results.length > 0 && (
                    <ResultsTable
                      title="Invitation results:"
                      columns={[
                        { key: 'row', label: 'Row' },
                        { key: 'email', label: 'Email' },
                        { key: 'outcome', label: 'Outcome' },
                        { key: 'message', label: 'Details' },
                      ]}
                      rows={result.data.results}
                    />
                  )}

                {result.data &&
                  result.data.errors &&
                  result.data.errors.length > 0 && (
                    <ResultsTable
                      title="Errors:"
                      columns={[
                        { key: 'row', label: 'Row' },
                        { key: 'email', label: 'Email' },
                        { key: 'error', label: 'Error' },
                      ]}
                      rows={result.data.errors}
                    />
                  )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
